<template>
  <!-- Title bar: stage + status beside the deliverable name -->
  <div v-if="part === 'title'" class="title-bar-status">
    <div class="inline-editable">
      <div v-if="!isEditingStage" class="clickable-field" @click="startEditingStage">
        <span class="tag" :class="getDeliverableStageClassName(formData.stage)">
          <span class="icon is-small mr-1">
            <font-awesome-icon :icon="['fal', getDeliverableStageIcon(formData.stage)]" />
          </span>
          {{ getDeliverableStageLabel(formData.stage) }}
          <span class="icon ml-1 edit-hint">
            <font-awesome-icon :icon="['fal', 'edit']" />
          </span>
        </span>
      </div>
      <div v-else class="inline-edit-field">
        <SearchableSelect
          v-model="tempStage"
          :items="stageOptionsForDisplay"
          value-field="value"
          display-field="label"
          placeholder="Select stage..."
          :hide-search="true"
          :show-icons="true"
          icon-field="icon"
          class="inline-select"
          @update:modelValue="saveField('stage', $event)"
        />
        <button class="button is-small" @click="isEditingStage = false">
          <span class="icon is-small"><font-awesome-icon :icon="['fal', 'times']" /></span>
        </button>
      </div>
    </div>
    <div class="inline-editable">
      <div v-if="!isEditingStatus" class="clickable-field" @click="startEditingStatus">
        <span class="tag" :class="getDeliverableStatusClassName(formData.status)">
          <span class="icon is-small mr-1">
            <font-awesome-icon :icon="['fal', getDeliverableStatusIcon(formData.status)]" />
          </span>
          {{ getDeliverableStatusLabel(formData.status) }}
          <span class="icon ml-1 edit-hint">
            <font-awesome-icon :icon="['fal', 'edit']" />
          </span>
        </span>
      </div>
      <div v-else class="inline-edit-field">
        <SearchableSelect
          v-model="tempStatus"
          :items="statusOptions"
          value-field="value"
          display-field="label"
          placeholder="Select status..."
          :hide-search="true"
          :show-icons="true"
          icon-field="icon"
          class="inline-select"
          @update:modelValue="saveField('status', $event)"
        />
        <button class="button is-small" @click="isEditingStatus = false">
          <span class="icon is-small"><font-awesome-icon :icon="['fal', 'times']" /></span>
        </button>
      </div>
    </div>
  </div>

  <!-- Asset column (Review tab): deliverable-level internal / client review status -->
  <div v-else-if="part === 'review'" class="review-status-summary">
    <div class="detail-item">
      <label class="label is-small">Internal</label>
      <span class="tag" :class="statusMeta(internalStatus).cardClass">{{ statusMeta(internalStatus).label }}</span>
    </div>
    <div class="detail-item">
      <label class="label is-small">Client</label>
      <span class="tag" :class="statusMeta(clientStatus).cardClass">{{ statusMeta(clientStatus).label }}</span>
    </div>
  </div>

  <!-- Tab row: people -->
  <div v-else class="deliverable-info-bar">
    <div class="info-bar-group">
      <div class="detail-item">
        <label class="label is-small">Assignee</label>
        <UserAvatarSelect
          :modelValue="formData.assignedUserId"
          size="small"
          :clearable="false"
          @select="saveField('assignedUserId', $event?.id || null)"
        />
      </div>
      <div class="detail-item">
        <label class="label is-small">Editor</label>
        <UserAvatarSelect
          :modelValue="formData.primaryEditorId"
          size="small"
          :clearable="false"
          @select="saveField('primaryEditorId', $event?.id || null)"
        />
      </div>
      <div class="detail-item">
        <label class="label is-small">Subscribers</label>
        <div class="is-flex is-align-items-center" style="gap: 0.25rem;">
          <UserAvatarSelect
            v-for="(sub, index) in subscribers"
            :key="sub"
            :model-value="sub"
            size="small"
            :disabled="subscribers.length === 1"
            :clearable="subscribers.length > 1"
            :admin-only="false"
            :company-id="companyId"
            @update:model-value="(value) => updateSubscriber(index, value)"
          />
          <UserAvatarSelect
            v-if="companyId"
            :model-value="null"
            size="small"
            placeholder="+"
            :admin-only="false"
            :company-id="companyId"
            @select="addSubscriber"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Deliverable status controls that live in DeliverableModal's chrome (title bar
// + tab row) so they show on every tab, not just Review.
import { ref, computed } from 'vue'
import { useAdminProjectsStore } from '@/stores/adminProjectsStore'
import { useAdminReviewStore } from '@/stores/adminReviewStore'
import { logger } from '@/services/loggerService'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import UserAvatarSelect from '@/components/common/UserAvatarSelect.vue'
import { REVIEW_ITEM_STATUS, getEpisodeSlotCompleteness } from '@/utils/reviewUtils'
import {
  getDeliverableStageOptions,
  getDeliverableStatusOptions,
  getDeliverableStageIcon,
  getDeliverableStageClassName,
  getDeliverableStageLabel,
  getDeliverableStatusIcon,
  getDeliverableStatusClassName,
  getDeliverableStatusLabel,
  DELIVERABLE_STAGES,
} from '@/utils/deliverableUtils'

const props = defineProps({
  part: { type: String, default: 'toolbar' }, // 'title' | 'toolbar' | 'review'
  deliverable: { type: Object, required: true },
  deliverableForm: { type: Object, default: null },
  project: { type: Object, default: null },
  company: { type: Object, default: null },
})

const projectsStore = useAdminProjectsStore()
const reviewStore = useAdminReviewStore()

const formData = computed(() => props.deliverableForm || props.deliverable)
const liveProject = computed(() => {
  const id = props.project?.id || props.deliverable?.projectId
  const fromStore = id ? projectsStore.items?.[id] : null
  return fromStore?.id ? fromStore : props.project
})
const companyId = computed(() =>
  props.company?.id || liveProject.value?.company || props.deliverable?.companyId || null
)

// Review items are only in memory once the Review tab has loaded this
// deliverable's review; until then use the aggregates stored on the doc.
const reviewItems = computed(() =>
  reviewStore.review?.id === props.deliverable?.id && reviewStore.initialized ? reviewStore.sortedItems : null
)

async function saveField(field, value) {
  if (!props.deliverable?.id) return
  try {
    await projectsStore.updateDeliverable(props.deliverable.id, { [field]: value })
    logger.info('[DeliverableStatusBar] Field updated', { field, value })
  } catch (error) {
    logger[error.isValidation ? 'warn' : 'error']('[DeliverableStatusBar] Failed to update field', { field, error: error.message })
  } finally {
    if (field === 'stage') isEditingStage.value = false
    if (field === 'status') isEditingStatus.value = false
  }
}

// --- Stage / status inline edit ---
const stageOptions = getDeliverableStageOptions()
const statusOptions = getDeliverableStatusOptions()
const isEditingStage = ref(false)
const isEditingStatus = ref(false)
const tempStage = ref('')
const tempStatus = ref('')

function startEditingStage() {
  tempStage.value = formData.value?.stage || ''
  isEditingStage.value = true
}
function startEditingStatus() {
  tempStatus.value = formData.value?.status || ''
  isEditingStatus.value = true
}

// Episode reviews can't move to Client Review until every slot is filled +
// internally approved (deliverableService enforces it; this just hides the option).
const episodeSlotsReady = computed(() => {
  const items = reviewItems.value
  const isEpisode = props.deliverable?.isPrimaryEpisodeDeliverable && liveProject.value?.type === 'episode'
  if (!isEpisode || !items || (items.length > 0 && !items.some((i) => i.slot))) return true
  return getEpisodeSlotCompleteness(items, liveProject.value || {}, {
    requireApproved: true,
    clientProvidesThumbnail: !!props.deliverable?.clientProvidesThumbnail,
    anthemDistributes: props.deliverable?.anthemDistributes !== false,
  }).isComplete
})
const stageOptionsForDisplay = computed(() =>
  episodeSlotsReady.value ? stageOptions : stageOptions.filter((o) => o.value !== DELIVERABLE_STAGES.CLIENT_REVIEW)
)

// --- Subscribers (on the project) ---
const subscribers = computed(() => (liveProject.value?.subscribers || []).filter(Boolean))

async function writeSubscribers(next) {
  const projectId = liveProject.value?.id || props.deliverable?.projectId
  if (!projectId) return
  try {
    await projectsStore.updateProject(projectId, { subscribers: next })
  } catch (error) {
    logger.error('[DeliverableStatusBar] Failed to update subscribers', { error: error.message })
  }
}
function addSubscriber(user) {
  const userId = user?.id || user
  if (!userId || subscribers.value.includes(userId)) return
  writeSubscribers([...subscribers.value, userId])
}
function updateSubscriber(index, newUserId) {
  const next = [...subscribers.value]
  if (!newUserId) {
    if (next.length <= 1) return
    next.splice(index, 1)
  } else {
    next[index] = newUserId
  }
  writeSubscribers(next)
}

// --- Internal / client review status ---
function aggregate(field, order) {
  const items = reviewItems.value
  if (!items) return null
  if (items.length === 0) return null
  for (const status of order) {
    if (items.some((i) => i[field] === status)) return status
  }
  if (items.some((i) => !i[field] || i[field] === REVIEW_ITEM_STATUS.PENDING_REVIEW)) return REVIEW_ITEM_STATUS.PENDING_REVIEW
  return REVIEW_ITEM_STATUS.APPROVED
}
const internalStatus = computed(() =>
  aggregate('internalStatus', [REVIEW_ITEM_STATUS.CHANGES_REQUESTED, REVIEW_ITEM_STATUS.IN_REVISION])
    ?? props.deliverable?.internalReviewStatus ?? null
)
const clientStatus = computed(() =>
  aggregate('clientStatus', [REVIEW_ITEM_STATUS.CHANGES_REQUESTED, REVIEW_ITEM_STATUS.PENDING_FINALIZATION])
    ?? props.deliverable?.clientReviewStatus ?? null
)

function statusMeta(status) {
  switch (status) {
    case REVIEW_ITEM_STATUS.APPROVED: return { label: 'Approved', cardClass: 'is-success' }
    case REVIEW_ITEM_STATUS.CHANGES_REQUESTED: return { label: 'Changes', cardClass: 'is-danger' }
    case REVIEW_ITEM_STATUS.IN_REVISION: return { label: 'In revision', cardClass: 'is-warning' }
    case REVIEW_ITEM_STATUS.PENDING_FINALIZATION: return { label: 'Finalizing', cardClass: 'is-warning' }
    default: return { label: 'Pending', cardClass: 'is-info' }
  }
}
</script>

<style lang="scss" scoped>
.title-bar-status {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.75rem;
  // Title bar is uppercase/tracked; the tags shouldn't inherit that
  text-transform: none;
  letter-spacing: normal;
  font-weight: normal;
  vertical-align: middle;
}

.deliverable-info-bar {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  min-width: 0;
  flex-wrap: wrap;
  row-gap: 0.5rem;

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;

    .label {
      margin-bottom: 0;
      font-size: 0.6rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bulma-text-weak, #6b7280);
    }
  }
}

.info-bar-group {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.review-status-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  min-width: 0;

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .label {
    margin-bottom: 0;
    font-size: 0.6rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--bulma-text-weak, #6b7280);
  }
}

.inline-editable {
  .clickable-field {
    cursor: pointer;
    padding: 0.15rem;
    border-radius: 4px;

    &:hover .edit-hint {
      opacity: 1;
    }
  }

  .edit-hint {
    font-size: 0.7rem;
    opacity: 0;
    transition: opacity 0.15s ease;
  }
}

.inline-edit-field {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  position: relative;
  z-index: 100;
}

.inline-select {
  min-width: 180px;
}
</style>
